/**
 * Error Distribution Chart Component
 *
 * Pie chart showing errors broken down by type
 */

import { Pie, PieChart } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { CHART_HEIGHT_COMPACT_CLASSES } from './ChartCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { ErrorByType } from '@/types/dashboard';

interface ErrorDistributionChartProps {
  data: ErrorByType[] | undefined;
  isLoading: boolean;
  errorRate?: number;
  totalErrors?: number;
}

// All error types that can occur
// Note: We use 'errorType' as the field name to avoid collision with Recharts'
// internal 'type' property on legend items
const ERROR_TYPES = [
  'retrieval_error',
  'llm_error',
  'filter_error',
  'validation_error',
  'unknown',
] as const;

const chartConfig = {
  count: {
    label: 'Errors',
  },
  retrieval_error: {
    label: 'Retrieval',
    color: 'var(--chart-1)',
  },
  llm_error: {
    label: 'LLM',
    color: 'var(--chart-2)',
  },
  filter_error: {
    label: 'Filter',
    color: 'var(--chart-3)',
  },
  validation_error: {
    label: 'Validation',
    color: 'var(--chart-4)',
  },
  unknown: {
    label: 'Unknown',
    color: 'var(--chart-5)',
  },
  no_errors: {
    label: 'No Errors',
    color: 'hsl(var(--muted))',
  },
} satisfies ChartConfig;

// Placeholder data for when there are no errors
// fill uses var(--color-no_errors) to match the config key
const emptyChartData = [
  { errorType: 'no_errors', count: 100, fill: 'var(--color-no_errors)' },
];

/**
 * Format error rate for display
 */
function formatErrorRate(rate: number | undefined): string {
  if (rate === undefined) return '-';
  if (rate < 0.1 && rate > 0) return '<0.1%';
  return `${rate.toFixed(1)}%`;
}

export function ErrorDistributionChart({
  data,
  isLoading,
  errorRate,
  totalErrors,
}: ErrorDistributionChartProps) {
  // Build a map of existing error counts
  const errorMap = new Map<string, number>();
  data?.forEach((item) => {
    errorMap.set(item.type, item.count);
  });

  // Generate chart data for all error types (with 0 for missing types)
  // fill uses var(--color-{errorType}) to match the config keys exactly
  const chartData = ERROR_TYPES.map((errorType) => ({
    errorType,
    count: errorMap.get(errorType) ?? 0,
    fill: `var(--color-${errorType})`,
  }));

  const chartTotalErrors = chartData.reduce((sum, item) => sum + item.count, 0);
  const hasErrors = chartTotalErrors > 0;

  // Show skeleton when loading OR when data hasn't arrived yet (prop is undefined)
  const showSkeleton = isLoading || data === undefined;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-1">
          <CardTitle>Error Distribution</CardTitle>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex cursor-help"
                aria-label="Learn more about Error Distribution"
              >
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p>{METRIC_DESCRIPTIONS.errorDistribution}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {!showSkeleton && errorRate !== undefined && (
          <div className="flex gap-4 text-sm">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`inline-flex items-center gap-1 cursor-help ${
                    errorRate >= 10
                      ? 'text-destructive'
                      : errorRate >= 5
                        ? 'text-warning'
                        : 'text-muted-foreground'
                  }`}
                >
                  Error Rate: {formatErrorRate(errorRate)}
                  <HelpCircle
                    className="h-3 w-3 opacity-60"
                    aria-hidden="true"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <p>{METRIC_DESCRIPTIONS.errorRate}</p>
              </TooltipContent>
            </Tooltip>
            {totalErrors !== undefined && totalErrors > 0 && (
              <span className="text-muted-foreground">
                ({totalErrors.toLocaleString()} total)
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {showSkeleton ? (
          <Skeleton className={`${CHART_HEIGHT_COMPACT_CLASSES} w-full`} />
        ) : (
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square h-[200px] sm:h-[250px]"
          >
            <PieChart>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideLabel
                    formatter={(value, name, item) => {
                      const indicatorColor =
                        ((item.payload as Record<string, unknown>)
                          ?.fill as string) || item.color;
                      const label =
                        chartConfig[name as keyof typeof chartConfig]?.label ||
                        name;
                      const displayValue =
                        name === 'no_errors'
                          ? '100%'
                          : typeof value === 'number'
                            ? value.toLocaleString()
                            : value;

                      return (
                        <>
                          <div
                            className="shrink-0 rounded-[2px] h-2.5 w-2.5"
                            style={{ backgroundColor: indicatorColor }}
                          />
                          <div className="flex flex-1 justify-between items-center leading-none">
                            <span className="text-muted-foreground">
                              {label}
                            </span>
                            <span className="text-foreground font-mono font-medium tabular-nums">
                              {displayValue}
                            </span>
                          </div>
                        </>
                      );
                    }}
                  />
                }
              />
              <Pie
                data={hasErrors ? chartData : emptyChartData}
                dataKey="count"
                nameKey="errorType"
              />
              <ChartLegend
                content={<ChartLegendContent nameKey="errorType" />}
                className="-translate-y-2 flex-wrap gap-2 *:basis-1/3 *:justify-center"
              />
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
